import React from 'react';
import * as ReactDOMClient from 'react-dom/client';

import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
// Components
import App from './App';
// Styles
import CssBaseline from '@mui/material/CssBaseline';
import './index.css';
import './i18n';
import './configs';

// Global polyfill for Playerjs browser detection variables
// Fixes "xbrowser/swbrowser is not defined" errors in WebViews
try {
  const ua = navigator.userAgent?.toLowerCase() || '';
  const browserPolyfill = {
    isAndroid: /android/i.test(ua),
    isIOS: /iphone|ipad|ipod/i.test(ua),
    isMobile: /android|iphone|ipad|ipod|mobile/i.test(ua),
    isChrome: /chrome/i.test(ua) && !/edge/i.test(ua),
    isSafari: /safari/i.test(ua) && !/chrome/i.test(ua),
    isFirefox: /firefox/i.test(ua),
    isEdge: /edge/i.test(ua),
    isIE: /msie|trident/i.test(ua),
    isWebView: /wv|webview/i.test(ua),
    // Additional Playerjs methods
    isAllowAutoFillOnDomain: (domain?: string) => {
      try {
        return domain ? window.location.hostname === domain : false;
      } catch (e) {
        return false;
      }
    },
    loadAutoFillFormData: () => {
      // Some Android WebViews (e.g. TCL Z2576) invoke this synchronously and
      // pass the return value straight to JSON.parse. Returning a Promise here
      // stringified to "[object Promise]" and crashed the WebView's autofill.
      return '{}';
    },
    needInjectCss: () => {
      return false;
    },
  };

  if (typeof (window as any).xbrowser === 'undefined') {
    (window as any).xbrowser = browserPolyfill;
  }
  if (typeof (window as any).swbrowser === 'undefined') {
    (window as any).swbrowser = browserPolyfill;
  }
} catch (e) {
  console.warn('Browser polyfill initialization failed:', e);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const rootElement = document.getElementById('root')!;
const root = ReactDOMClient.createRoot(rootElement);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <CssBaseline />
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

window.addEventListener('vite:preloadError', () => {
  // Let lazyRetry catch the error and run its retry/reload flow.
  // Calling preventDefault() here made __vitePreload resolve with undefined,
  // which left React.lazy with _result === undefined and crashed on _result.default.
});

// Global error handler for JSON parsing issues non indeintified by Sentry
window.addEventListener('error', (event: ErrorEvent) => {
  try {
    const msg = event?.message || '';
    const stack = (event?.error && event.error.stack) || '';

    console.warn(
      'Global error handler caught an error event: ',
      JSON.stringify(event),
    );
    // Handle JSON parsing errors
    if (
      typeof msg === 'string' &&
      (msg.includes('is not valid JSON') ||
        msg.includes('[object Promise]') ||
        msg.includes('Unexpected token') ||
        msg.includes('ResizeObserver loop limit exceeded'))
    ) {
      // Prevent the error from propagating if it's just a warning
      event.preventDefault();
    }
  } catch (e) {
    // Ignore error handler errors
  }
});
